import React, { useEffect, useRef, useState } from 'react';
import { BsChevronDown, BsChevronUp } from 'react-icons/bs';
import { TextInput, Pagination } from 'flowbite-react';
import { generatePositionTitle } from 'shared-utils';

interface RejectedPosition {
    positionId: string;
    positionName: string;
    feedback?: string;
}

interface RejectedCompany {
    companyId: string;
    companyName: string;
    rejectedCount: number;
    positions: RejectedPosition[];
}

interface RejectedSectionProps {
    rejectedData: RejectedCompany[];
    rejectedMeta?: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
    };
    currentPage: number;
    isLoading?: boolean;
    onSearchChange?: (value: string) => void;
    onPageChange?: (page: number) => void;
    controlledSearch?: string;
}

const RejectedSection: React.FC<RejectedSectionProps> = ({
    rejectedData,
    rejectedMeta,
    currentPage,
    isLoading = false,
    onSearchChange,
    onPageChange,
    controlledSearch = '',
}) => {
    const [expandedCompany, setExpandedCompany] = useState<string | null>(null);
    const [expandedPositions, setExpandedPositions] = useState<Set<string>>(new Set());
    const [searchTerm, setSearchTerm] = useState(controlledSearch);
    const onSearchChangeRef = useRef(onSearchChange);

    // Keep the latest onSearchChange without re-running effects
    useEffect(() => {
        onSearchChangeRef.current = onSearchChange;
    }, [onSearchChange]);

    const handleToggleCompany = (companyId: string) => {
        setExpandedCompany(expandedCompany === companyId ? null : companyId);
    };

    const handleTogglePosition = (positionId: string) => {
        setExpandedPositions(prev => {
            const newSet = new Set(prev);
            if (newSet.has(positionId)) {
                newSet.delete(positionId);
            } else {
                newSet.add(positionId);
            }
            return newSet;
        });
    };

    // Debounce search to avoid firing API on every keystroke
    useEffect(() => {
        const handler = onSearchChangeRef.current;
        if (!handler) return;
        const timeoutId = setTimeout(() => {
            handler(searchTerm);
        }, 400);
        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    // Sync search term with controlled value
    useEffect(() => {
        setSearchTerm(controlledSearch);
    }, [controlledSearch]);

    const totalRejected = rejectedMeta?.total || 0;
    const totalPages = Math.max(1, Math.ceil((rejectedMeta?.total || 0) / (rejectedMeta?.limit || 5)));

    // Format position name for display
    const formatPositionName = (positionName: string): string => {
        // First try the generatePositionTitle function (handles enum values)
        const formatted = generatePositionTitle(positionName);

        // If it didn't change (not in enum), format manually
        if (formatted === positionName) {
            // Convert underscores to spaces and title case
            return positionName
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ');
        }

        return formatted;
    };

    return (
        <div className="w-full">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                <h2 className="text-xl font-inter font-semibold dark:text-white text-[#28293D]">
                    Rejected {totalRejected > 0 && `(${totalRejected})`}
                </h2>
                <div className="w-full md:max-w-[360px]">
                    <TextInput
                        value={searchTerm}
                        onChange={e => {
                            setSearchTerm(e.target.value);
                        }}
                        placeholder="Search by company, position, or feedback"
                        sizing="md"
                        className="font-inter"
                    />
                </div>
            </div>

            {rejectedData.length === 0 ? (
                <div className="bg-white dark:bg-[#1F2A37] border border-[#d1d5db] dark:border-[#555770] rounded-xl px-6 py-8 text-center">
                    <p className="text-sm font-inter text-[#555770] dark:text-[#E4E4EB]">
                        {isLoading ? 'Loading...' : 'No rejected applications found'}
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    {isLoading && (
                        <div className="text-center text-sm font-inter text-[#555770] dark:text-[#E4E4EB]">
                            Loading...
                        </div>
                    )}
                    {rejectedData.map(company => (
                        <div
                            key={company.companyId}
                            className="bg-white dark:bg-[#1F2A37] border border-[#d1d5db] dark:border-[#555770] rounded-xl overflow-hidden"
                        >
                            <button
                                onClick={() => handleToggleCompany(company.companyId)}
                                className="w-full flex items-center justify-between px-6 py-4 hover:bg-[#f9fafb] dark:hover:bg-[#2a3441] transition-colors"
                            >
                                <div className="flex items-center gap-4">
                                    <span className="text-base font-inter font-medium dark:text-white text-[#28293D]">
                                        {company.companyName}
                                    </span>
                                    <span className="text-sm font-inter text-[#555770] dark:text-[#E4E4EB]">
                                        ({company.rejectedCount} {company.rejectedCount === 1 ? 'rejection' : 'rejections'})
                                    </span>
                                </div>
                                {expandedCompany === company.companyId ? (
                                    <BsChevronUp className="text-[#555770] dark:text-[#E4E4EB]" />
                                ) : (
                                    <BsChevronDown className="text-[#555770] dark:text-[#E4E4EB]" />
                                )}
                            </button>

                            {expandedCompany === company.companyId && (
                                <div className="border-t border-[#d1d5db] dark:border-[#555770] bg-[#f9fafb] dark:bg-[#2a3441]">
                                    {company.positions && company.positions.length > 0 ? (
                                        <div className="px-6 py-4 space-y-3">
                                            {company.positions.map((position) => (
                                                <div
                                                    key={position.positionId}
                                                    className="bg-white dark:bg-[#1F2A37] border border-[#d1d5db] dark:border-[#555770] rounded-lg overflow-hidden"
                                                >
                                                    <button
                                                        onClick={() => handleTogglePosition(position.positionId)}
                                                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#f9fafb] dark:hover:bg-[#2a3441] transition-colors"
                                                    >
                                                        <span className="text-sm font-inter font-medium dark:text-white text-[#28293D] text-left">
                                                            {formatPositionName(position.positionName)}
                                                        </span>
                                                        {position.feedback && (
                                                            expandedPositions.has(position.positionId) ? (
                                                                <BsChevronUp className="text-[#555770] dark:text-[#E4E4EB] flex-shrink-0 ml-2" />
                                                            ) : (
                                                                <BsChevronDown className="text-[#555770] dark:text-[#E4E4EB] flex-shrink-0 ml-2" />
                                                            )
                                                        )}
                                                    </button>
                                                    {expandedPositions.has(position.positionId) && position.feedback && (
                                                        <div className="px-4 py-3 border-t border-[#d1d5db] dark:border-[#555770] bg-[#f9fafb] dark:bg-[#2a3441]">
                                                            <h5 className="text-xs font-inter font-semibold dark:text-[#E4E4EB] text-[#555770] mb-1">
                                                                Feedback:
                                                            </h5>
                                                            <p className="text-sm font-inter text-[#28293D] dark:text-white">{position.feedback}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="px-6 py-4">
                                            <p className="text-sm font-inter text-[#555770] dark:text-[#E4E4EB]">
                                                No position details available
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                    {totalPages > 1 && rejectedMeta && onPageChange && (
                        <div className="pt-2 flex justify-center">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={(p) => onPageChange?.(p)}
                                showIcons
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default RejectedSection;

